export const FUNDING_EVIDENCE_ENDPOINT = "https://admin-api-testnet.aevo.xyz/account";

type ObjectValue = Record<string, unknown>;

function object(value: unknown): ObjectValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Account evidence is unavailable.");
  }
  return value as ObjectValue;
}

function scalar(value: unknown, type: "string" | "boolean", numeric = false) {
  const validType = type === "string" ? typeof value === "string" : typeof value === "boolean";
  if (!validType || (typeof value === "string" &&
      (value.length > 256 || (numeric && !/^-?[0-9]+(?:\.[0-9]+)?$/.test(value))))) {
    throw new Error("Required account evidence is missing or invalid.");
  }
  return value;
}

function rows(value: unknown, fields: readonly string[]) {
  if (!Array.isArray(value) || value.length > 1000) {
    throw new Error("Account evidence list is missing or too large.");
  }
  return value.map((entry) => {
    const row = object(entry);
    const result: ObjectValue = {};
    fields.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        result[key] = scalar(row[key], "string");
      }
    });
    return result;
  });
}

export function fundingEvidenceEnabled(environment: string | undefined, endpoint: string) {
  return environment === "testnet" && endpoint === FUNDING_EVIDENCE_ENDPOINT;
}

// A projection, never a dump of the response: additions to the API cannot expose
// credentials, signing keys, email, auth headers, or unknown nested properties.
export function createFundingEvidence(
  value: unknown,
  requestedAccount: string,
  capture: { endpoint: string; frontendOrigin: string; capturedAt: string }
) {
  const source = object(value);
  if (capture.endpoint !== FUNDING_EVIDENCE_ENDPOINT || source.error ||
      !/^0x[0-9a-fA-F]{40}$/.test(requestedAccount) ||
      typeof source.account !== "string" || source.account.toLowerCase() !== requestedAccount.toLowerCase() ||
      new Date(capture.capturedAt).toISOString() !== capture.capturedAt ||
      new URL(capture.frontendOrigin).origin !== capture.frontendOrigin ||
      !capture.frontendOrigin.startsWith("https://")) {
    throw new Error("Account evidence identity or source is invalid.");
  }
  const result: ObjectValue = {};
  ["account", "account_type", "user_group"].forEach((key) => {
    result[key] = scalar(source[key], "string");
  });
  ["balance", "available_balance", "credit", "staked_amount", "fee_discount"].forEach((key) => {
    result[key] = scalar(source[key], "string", true);
  });
  ["portfolio", "in_liquidation", "manual_mode", "account_restricted", "has_been_referred"].forEach((key) => {
    result[key] = scalar(source[key], "boolean");
  });
  // Referrer and pending_withdrawals are omitempty in the real backend response.
  if (Object.prototype.hasOwnProperty.call(source, "referrer")) {
    result.referrer = scalar(source.referrer, "string");
  }
  result.positions = rows(source.positions, ["instrument_id", "instrument_name", "side", "amount"]);
  result.in_adl = rows(source.in_adl, ["instrument_id", "instrument_name"]);
  if (!Array.isArray(source.collaterals) || source.collaterals.length > 100) {
    throw new Error("Collateral evidence is missing or too large.");
  }
  result.collaterals = source.collaterals.map((entry) => {
    const row = object(entry);
    const collateral: ObjectValue = { collateral_asset: scalar(row.collateral_asset, "string") };
    ["balance", "available_balance"].forEach((key) => {
      collateral[key] = scalar(row[key], "string", true);
    });
    collateral.collateral_yield_bearing = scalar(row.collateral_yield_bearing, "boolean");
    if (Object.prototype.hasOwnProperty.call(row, "pending_withdrawals")) {
      collateral.pending_withdrawals = scalar(row.pending_withdrawals, "string", true);
    }
    return collateral;
  });
  Object.assign(result, { _capture: {
    type: "authenticated_admin_account_allowlisted_subset_v1",
    endpoint: capture.endpoint,
    frontend_origin: capture.frontendOrigin,
    account_requested: requestedAccount,
    captured_at: capture.capturedAt,
    timestamp_meaning: "browser export time, not HTTP receipt or backend sequence time",
    source: "loaded account response through the existing Google-authenticated Account page",
    representation: "allowlisted fields with original scalar values; positions and ADL rows are allowlisted projections",
  } });
  const text = JSON.stringify(result, null, 2);
  if (text.length > 262144) throw new Error("Account evidence is too large.");
  return text;
}
